import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import {
    collection,
    deleteDoc,
    doc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    Timestamp,
    updateDoc,
    where,
    writeBatch,
} from 'firebase/firestore';
import './AdminPromotions.css';

const PAGE_SIZE = 10;
const HISTORY_PAGE_SIZE = 8;
const MAIL_BATCH_LIMIT = 450;

// ─── syncPromotionStatuses ────────────────────────────────────────────────────
/**
 * Runs on every page load. Checks every promotion's startDate and endDate
 * against today and corrects the status field in Firestore if needed.
 *
 * Rules:
 *   "scheduled" + now >= startDate  →  "active"
 *   "active"    + now >  endDate    →  "inactive"
 *
 * Returns the updated promotions array (with corrected statuses) so the
 * local state can reflect the changes immediately without a second fetch.
 */
async function syncPromotionStatuses(promotions) {
    const now = new Date();
    const updates = [];

    for (const promo of promotions) {
        const startDate = toDate(promo.startDate);
        const endDate   = toDate(promo.endDate);
        const current   = promo.status;
        let   next      = current;

        // Expired: active/scheduled but end date has passed
        if (['active', 'scheduled'].includes(current) && endDate && now > endDate) {
            next = 'inactive';
        }
        // Activated: scheduled and start date has arrived
        else if (current === 'scheduled' && startDate && now >= startDate) {
            next = 'active';
        }

        if (next !== current) {
            updates.push({ id: promo.id, newStatus: next });
        }
    }

    // Batch-write all changes to Firestore
    await Promise.all(
        updates.map(({ id, newStatus }) =>
            updateDoc(doc(db, 'promotions', id), {
                status:    newStatus,
                updatedAt: serverTimestamp(),
            }).catch(err => console.error(`syncPromotions: failed for ${id}:`, err))
        )
    );

    // Return a new array with the corrected statuses applied locally
    if (updates.length === 0) return promotions;

    const updateMap = new Map(updates.map(u => [u.id, u.newStatus]));
    return promotions.map(p =>
        updateMap.has(p.id) ? { ...p, status: updateMap.get(p.id) } : p
    );
}

// ─── toDate helper ────────────────────────────────────────────────────────────
// Handles all the date formats Firestore can return:
// Timestamp object, plain {seconds, nanoseconds}, ISO string, JS Date, number
function toDate(value) {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    if (value.seconds !== undefined) return new Date(value.seconds * 1000);
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
}

function formatDate(value) {
    if (!value) return '—';
    const date = value.toDate ? value.toDate() : new Date(value);
    return date.toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatDateTime(value) {
    if (!value) return '—';
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-PH', { month: 'short', day: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function buildHtmlEmailBody(text) {
    const escaped = String(text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<pre style="font-family:sans-serif;white-space:pre-wrap;">${escaped}</pre>`;
}

function buildScheduledDate(dateValue, timeValue) {
    if (!dateValue || !timeValue) return null;
    const [year, month, day] = dateValue.split('-').map(Number);
    const [hours, minutes] = timeValue.split(':').map(Number);
    if ([year, month, day, hours, minutes].some((value) => Number.isNaN(value))) return null;
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

function getTodayDateInput() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function chunkItems(items, chunkSize) {
    const chunks = [];
    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }
    return chunks;
}

async function queueImmediateCampaign({ recipients, campaignData }) {
    const campaignRef = doc(collection(db, 'adminEmailCampaigns'));

    await setDoc(campaignRef, {
        ...campaignData,
        scheduled: false,
        sendAt: null,
        recipientCount: recipients.length,
        recipients,
        campaignStatus: 'queued',
        queuedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    });

    const recipientChunks = chunkItems(recipients, MAIL_BATCH_LIMIT);

    for (const recipientChunk of recipientChunks) {
        const batch = writeBatch(db);

        recipientChunk.forEach((email) => {
            const mailRef = doc(collection(db, 'mail'));
            batch.set(mailRef, {
                to: email,
                message: {
                    subject: campaignData.subject,
                    text: campaignData.messageText,
                    html: campaignData.messageHtml,
                },
                createdAt: serverTimestamp(),
                emailType: campaignData.emailType,
                promoId: campaignData.promoId,
                promoCode: campaignData.promoCode,
                promotionName: campaignData.promotionName,
                sentByAdmin: true,
                campaignId: campaignRef.id,
                scheduled: false,
            });
        });

        await batch.commit();
    }
}

function getCampaignBadge(item) {
    const status = item.campaignStatus || (item.scheduled ? 'scheduled' : 'sent');
    if (status === 'scheduled') return { label: 'Scheduled', className: 'promo-history-badge--scheduled' };
    if (status === 'pending') return { label: 'Processing', className: 'promo-history-badge--scheduled' };
    if (status === 'queued') return { label: 'Queued', className: 'promo-history-badge--sent' };
    if (status === 'error') return { label: 'Error', className: 'promo-history-badge--scheduled' };
    return { label: 'Sent', className: 'promo-history-badge--sent' };
}

function getCampaignStatusText(item) {
    const status = item.campaignStatus || (item.scheduled ? 'scheduled' : 'sent');
    if (status === 'scheduled') return `Send later: ${formatDateTime(item.sendAt)}`;
    if (status === 'pending') return 'Processing delivery queue';
    if (status === 'queued') return `Queued at: ${formatDateTime(item.queuedAt || item.updatedAt || item.sendAt)}`;
    return 'Delivered immediately';
}

function StatusBadge({ status }) {
    const map = {
        active: 'promo-badge--active',
        inactive: 'promo-badge--inactive',
        scheduled: 'promo-badge--scheduled',
    };
    return <span className={`promo-badge ${map[status] || 'promo-badge--inactive'}`}>{status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}</span>;
}

function CampaignHistoryModal({ onClose, emailType, promoId = null, title, subtitle }) {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);

    useEffect(() => {
        let active = true;
        const fetchHistory = async () => {
            setLoading(true);
            try {
                const snap = await getDocs(query(collection(db, 'adminEmailCampaigns'), orderBy('createdAt', 'desc')));
                if (!active) return;
                setItems(
                    snap.docs
                        .map((campaignDoc) => ({ id: campaignDoc.id, ...campaignDoc.data() }))
                        .filter((item) => item.emailType === emailType)
                        .filter((item) => !promoId || item.promoId === promoId)
                );
            } catch (err) {
                console.error('Failed to load campaign history:', err);
                if (active) setItems([]);
            } finally {
                if (active) setLoading(false);
            }
        };
        fetchHistory();
        return () => { active = false; };
    }, [emailType, promoId]);

    const totalPages = Math.max(1, Math.ceil(items.length / HISTORY_PAGE_SIZE));
    const pageItems = items.slice((page - 1) * HISTORY_PAGE_SIZE, page * HISTORY_PAGE_SIZE);

    return (
        <div className="promo-modal-overlay" onClick={onClose}>
            <div className="promo-history-modal" onClick={(event) => event.stopPropagation()}>
                <div className="promo-history-modal__header">
                    <div>
                        <h3>{title}</h3>
                        <p>{subtitle}</p>
                    </div>
                    <button className="promo-email-modal__close" onClick={onClose}>✕</button>
                </div>
                <div className="promo-history-modal__body">
                    {loading ? <div className="promo-history-empty">Loading announcement history…</div> : null}
                    {!loading && pageItems.length === 0 ? <div className="promo-history-empty">No email campaigns have been recorded yet.</div> : null}
                    {!loading && pageItems.map((item) => {
                        const badge = getCampaignBadge(item);
                        return (
                            <article key={item.id} className="promo-history-card">
                                <div className="promo-history-card__top">
                                    <div>
                                        <h4>{item.subject || 'Untitled announcement'}</h4>
                                        <p>{formatDateTime(item.createdAt)}</p>
                                    </div>
                                    <span className={`promo-history-badge ${badge.className}`}>{badge.label}</span>
                                </div>
                                <div className="promo-history-meta">
                                    <span>{item.recipientCount || 0} recipient{item.recipientCount === 1 ? '' : 's'}</span>
                                    <span>{getCampaignStatusText(item)}</span>
                                </div>
                                <div className="promo-history-message">{String(item.messageText || '').trim() || 'No message body saved.'}</div>
                            </article>
                        );
                    })}
                </div>
                {items.length > HISTORY_PAGE_SIZE ? (
                    <div className="promo-history-modal__footer">
                        <button className="promo-page-btn" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>← Previous</button>
                        <span className="promo-result-count">Page {page} of {totalPages}</span>
                        <button className="promo-page-btn" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>Next →</button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function SendEmailModal({ promo, defaultType = 'promoCode', onClose }) {
    const [emailType] = useState(defaultType);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [scheduleMode, setScheduleMode] = useState(false);
    const [scheduleDate, setScheduleDate] = useState('');
    const [scheduleTime, setScheduleTime] = useState('');
    const [sending, setSending] = useState(false);
    const [status, setStatus] = useState(null);
    const [recipientCount, setRecipientCount] = useState(null);

    useEffect(() => {
        if (emailType === 'promoCode') {
            setSubject(promo?.promoCode ? `🎉 Special Offer: Use ${promo.promoCode} for ${promo.discountValue}${promo.promotionType === 'percentage' ? '%' : '₱'} off!` : 'Exclusive Discount Just for You!');
            setMessage(promo?.promoCode ? `Hi there!\n\nWe have an exclusive discount just for you.\n\nUse code: ${promo.promoCode}\nDiscount: ${promo.discountValue}${promo.promotionType === 'percentage' ? '%' : '₱'} off${promo.discountedProducts ? ` on ${promo.discountedProducts}` : ''}\n${promo.endDate ? `Valid until: ${formatDate(promo.endDate)}` : ''}\n\nShop now and save!\n\n— JG Printing` : '');
        } else {
            setSubject('📢 Exciting News from JG Printing!');
            setMessage('Hi there!\n\nWe have an exciting announcement to share with you.\n\n[Write your announcement here]\n\n— JG Printing');
        }
    }, [emailType, promo]);

    useEffect(() => {
        const field = emailType === 'promoCode' ? 'promoCodeEmails' : 'announcementEmails';
        getDocs(query(collection(db, 'users'), where(field, '==', true))).then((snap) => setRecipientCount(snap.size)).catch(() => setRecipientCount(null));
    }, [emailType]);

    const canSend = !sending && subject.trim() && message.trim() && status !== 'success' && (!scheduleMode || (scheduleDate && scheduleTime));

    const handleSend = async () => {
        if (!subject.trim() || !message.trim()) return;
        if (scheduleMode) {
            if (!scheduleDate || !scheduleTime) return setStatus('schedule-incomplete');
            if (new Date(`${scheduleDate}T${scheduleTime}`) <= new Date()) return setStatus('schedule-past');
        }

        setSending(true);
        setStatus(null);

        try {
            const field = emailType === 'promoCode' ? 'promoCodeEmails' : 'announcementEmails';
            const usersSnap = await getDocs(query(collection(db, 'users'), where(field, '==', true)));
            if (usersSnap.empty) {
                setStatus('no-recipients');
                setSending(false);
                return;
            }

            const scheduledDate = scheduleMode ? buildScheduledDate(scheduleDate, scheduleTime) : null;
            const sendAt = scheduledDate ? Timestamp.fromDate(scheduledDate) : null;
            const recipients = Array.from(new Set(usersSnap.docs.map((userDoc) => String(userDoc.data().email || '').trim().toLowerCase()).filter(Boolean)));
            const campaignData = {
                emailType,
                subject: subject.trim(),
                messageText: message.trim(),
                messageHtml: buildHtmlEmailBody(message.trim()),
                promoId: promo?.id || null,
                promoCode: promo?.promoCode || '',
                promotionName: promo?.promotionName || '',
                sentByAdmin: true,
            };

            if (scheduleMode) {
                const campaignRef = doc(collection(db, 'adminEmailCampaigns'));
                await setDoc(campaignRef, {
                    ...campaignData,
                    scheduled: true,
                    sendAt: sendAt || null,
                    recipientCount: recipients.length,
                    recipients,
                    campaignStatus: 'scheduled',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                });
            } else {
                await queueImmediateCampaign({
                    recipients,
                    campaignData,
                });
            }

            setStatus('success');
        } catch (err) {
            console.error('Email send error:', err);
            setStatus('error');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="promo-modal-overlay" onClick={!sending ? onClose : undefined}>
            <div className="promo-email-modal" onClick={(event) => event.stopPropagation()}>
                <div className="promo-email-modal__header">
                    <div className="promo-email-modal__header-left">
                        <div className={`promo-email-modal__icon ${emailType === 'announcement' ? 'promo-email-modal__icon--announce' : ''}`}>
                            {emailType === 'announcement' ? '📢' : '✉'}
                        </div>
                        <div>
                            <h3 className="promo-email-modal__title">{emailType === 'announcement' ? 'Send Announcement Email' : 'Send Email'}</h3>
                            <p className="promo-email-modal__subtitle">{promo?.promoCode ? `Promotion: ${promo.promoCode}` : promo?.promotionName || 'JG Printing'}</p>
                        </div>
                    </div>
                    <button className="promo-email-modal__close" onClick={onClose} disabled={sending}>✕</button>
                </div>
                <div className="promo-email-modal__body">
                    <div className="promo-email-field">
                        <label className="promo-email-label">Email Type</label>
                        <div className={`promo-email-type-locked ${emailType === 'announcement' ? 'promo-email-type-locked--announce' : 'promo-email-type-locked--promo'}`}>
                            <div className={`promo-email-type-icon ${emailType === 'announcement' ? 'promo-email-type-icon--announce' : 'promo-email-type-icon--promo'}`}>{emailType === 'announcement' ? '📢' : '🏷'}</div>
                            <div>
                                <span className="promo-email-type-label">{emailType === 'announcement' ? 'Announcement Email' : 'Promo Code Email'}</span>
                                <span className="promo-email-type-desc">{emailType === 'announcement' ? 'Campaigns & new products' : 'Discount codes & offers'}</span>
                            </div>
                        </div>
                        <div className="promo-email-recipients">{recipientCount === null ? 'Counting recipients…' : recipientCount === 0 ? 'No users have opted in to this email type yet.' : `${recipientCount} user${recipientCount !== 1 ? 's' : ''} will receive this email`}</div>
                    </div>
                    <div className="promo-email-field">
                        <label className="promo-email-label">Subject <span className="promo-email-req">*</span></label>
                        <input className="promo-email-input" value={subject} onChange={(event) => setSubject(event.target.value)} disabled={sending} />
                    </div>
                    <div className="promo-email-field">
                        <label className="promo-email-label">Message <span className="promo-email-req">*</span></label>
                        <textarea className="promo-email-textarea" value={message} onChange={(event) => setMessage(event.target.value)} disabled={sending} rows={7} />
                    </div>
                    <div className="promo-email-field">
                        <label className="promo-email-schedule-toggle">
                            <input type="checkbox" className="promo-schedule-checkbox" checked={scheduleMode} onChange={(event) => {
                                setScheduleMode(event.target.checked);
                                if (!event.target.checked) {
                                    setScheduleDate('');
                                    setScheduleTime('');
                                    if (status === 'schedule-incomplete' || status === 'schedule-past') setStatus(null);
                                }
                            }} disabled={sending} />
                            <span className="promo-schedule-toggle-track" />
                            <span className="promo-email-label" style={{ margin: 0 }}>Schedule for later</span>
                        </label>
                    </div>
                    {scheduleMode ? (
                        <div className="promo-email-schedule-row">
                            <div className="promo-email-field" style={{ flex: 1 }}>
                                <label className="promo-email-label">Date <span className="promo-email-req">*</span></label>
                                <input type="date" className={`promo-email-input ${!scheduleDate ? 'promo-email-input--warn' : ''}`} min={getTodayDateInput()} value={scheduleDate} onChange={(event) => { setScheduleDate(event.target.value); setStatus(null); }} disabled={sending} />
                            </div>
                            <div className="promo-email-field" style={{ flex: 1 }}>
                                <label className="promo-email-label">Time <span className="promo-email-req">*</span></label>
                                <input type="time" className={`promo-email-input ${!scheduleTime ? 'promo-email-input--warn' : ''}`} value={scheduleTime} onChange={(event) => { setScheduleTime(event.target.value); setStatus(null); }} disabled={sending} />
                            </div>
                        </div>
                    ) : null}
                    {status === 'error' ? <div className="promo-email-status promo-email-status--error">Failed to send emails. Please try again.</div> : null}
                    {status === 'no-recipients' ? <div className="promo-email-status promo-email-status--warn">No users have opted in to this email type yet.</div> : null}
                    {status === 'schedule-incomplete' ? <div className="promo-email-status promo-email-status--warn">Please fill in both a date and time to schedule this email.</div> : null}
                    {status === 'schedule-past' ? <div className="promo-email-status promo-email-status--error">The scheduled time must be in the future. Please choose a later time.</div> : null}
                </div>
                <div className="promo-email-modal__footer">
                    <button className="promo-modal-cancel" onClick={onClose} disabled={sending}>Cancel</button>
                    <button className={`promo-email-send-btn ${scheduleMode ? 'promo-email-send-btn--schedule' : ''}`} onClick={handleSend} disabled={!canSend}>
                        {sending ? <><span className="promo-btn-spinner" /> {scheduleMode ? 'Scheduling…' : 'Sending…'}</> : scheduleMode ? 'Schedule Email' : 'Send Now'}
                    </button>
                </div>
                {status === 'success' ? (
                    <div className="promo-success-dialog-overlay">
                        <div className="promo-success-dialog">
                            <div className={`promo-success-dialog__icon ${scheduleMode ? 'promo-success-dialog__icon--schedule' : ''}`}>{scheduleMode ? '🕒' : '✓'}</div>
                            <h3 className="promo-success-dialog__title">{scheduleMode ? 'Email Scheduled!' : 'Emails Sent!'}</h3>
                            <p className="promo-success-dialog__msg">
                                {scheduleMode
                                    ? <><strong>{recipientCount}</strong> user{recipientCount !== 1 ? 's' : ''} will receive this email on <strong>{scheduleDate}</strong> at <strong>{scheduleTime}</strong>.</>
                                    : <>Your email has been sent successfully to <strong>{recipientCount}</strong> opted-in user{recipientCount !== 1 ? 's' : ''}.</>}
                            </p>
                            <button className="promo-success-dialog__btn" onClick={onClose}>Done</button>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default function AdminPromotions() {
    const navigate = useNavigate();
    const [promotions, setPromotions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [emailModal, setEmailModal] = useState(null);
    const [showAnnouncementHistory, setShowAnnouncementHistory] = useState(false);
    const [promoHistoryTarget, setPromoHistoryTarget] = useState(null);

    useEffect(() => { fetchPromotions(); }, []);

    async function fetchPromotions() {
        setLoading(true);
        try {
            const snap = await getDocs(query(collection(db, 'promotions'), orderBy('createdAt', 'desc')));
            const raw  = snap.docs.map((promotionDoc) => ({ id: promotionDoc.id, ...promotionDoc.data() }));

            // ── Auto-sync statuses based on start/end dates ──────────────────
            // "scheduled" promotions whose startDate has arrived → "active"
            // "active" promotions whose endDate has passed       → "inactive"
            const synced = await syncPromotionStatuses(raw);
            setPromotions(synced);
        } catch (err) {
            console.error('Error fetching promotions:', err);
            setPromotions([]);
        }
        setLoading(false);
    }

    const filtered = promotions.filter((promotion) => {
        const term = search.toLowerCase();
        return (promotion.promoCode || '').toLowerCase().includes(term)
            || (promotion.promotionName || '').toLowerCase().includes(term)
            || (promotion.discountedProducts || '').toLowerCase().includes(term);
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    async function handleDeleteExecute() {
        if (!deleteTarget) return;
        try {
            await deleteDoc(doc(db, 'promotions', deleteTarget.id));
            setPromotions((current) => current.filter((promotion) => promotion.id !== deleteTarget.id));
        } catch (err) {
            console.error('Error deleting promotion:', err);
        }
        setDeleteTarget(null);
    }

    return (
        <div className="promo-manage">
            <div className="promo-toolbar">
                <div className="promo-showing">Showing <span className="promo-count">{filtered.length}</span> promotion{filtered.length !== 1 ? 's' : ''}</div>
                <div className="promo-toolbar-right">
                    <div className="promo-search-wrap">
                        <input className="promo-search" placeholder="Search promotions…" value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
                        <svg className="promo-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    </div>
                    <button className="promo-btn-announce" onClick={() => setEmailModal({ promo: null, defaultType: 'announcement' })}>Send Announcement</button>
                    <button className="promo-btn-history" onClick={() => setShowAnnouncementHistory(true)}>View Sent Announcements</button>
                    <button className="promo-btn-add" onClick={() => navigate('/admin/promotions/details', { state: { promotion: null } })}>Add Promotion</button>
                </div>
            </div>
            <div className="promo-table-wrap">
                <table className="promo-table">
                    <thead><tr><th>#</th><th>Promo Code</th><th>Name</th><th>Type</th><th>Discount</th><th>Start Date</th><th>End Date</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                        {loading ? <tr><td colSpan={9} className="promo-table__empty"><div className="promo-loading"><div className="promo-spinner" />Loading promotions…</div></td></tr> : null}
                        {!loading && paginated.length === 0 ? <tr><td colSpan={9} className="promo-table__empty"><div className="promo-empty-state"><span className="promo-empty-title">No promotions found</span><span className="promo-empty-sub">Click "Add Promotion" to create your first one.</span></div></td></tr> : null}
                        {!loading && paginated.map((promo, idx) => (
                            <tr key={promo.id}>
                                <td className="promo-cell-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                                <td><span className="promo-code-chip">{promo.promoCode || '—'}</span></td>
                                <td className="promo-cell-name">{promo.promotionName || '—'}</td>
                                <td><span className="promo-type-badge">{promo.promotionType === 'percentage' ? 'Percentage' : promo.promotionType === 'fixed' ? 'Fixed' : '—'}</span></td>
                                <td className="promo-cell-discount">{promo.discountValue ? `${promo.promotionType === 'percentage' ? '' : '₱'}${promo.discountValue}${promo.promotionType === 'percentage' ? '%' : ''}` : '—'}</td>
                                <td className="promo-cell-date">{formatDate(promo.startDate)}</td>
                                <td className="promo-cell-date">{formatDate(promo.endDate)}</td>
                                <td><StatusBadge status={promo.status} /></td>
                                <td>
                                    <div className="promo-actions">
                                        <button className="promo-action-btn promo-action-btn--email" title="Send Promo Code Email" onClick={() => setEmailModal({ promo, defaultType: 'promoCode' })}>✉</button>
                                        <button className="promo-action-btn promo-action-btn--history" title="View Promo Email History" onClick={() => setPromoHistoryTarget(promo)}>H</button>
                                        <button className="promo-action-btn promo-action-btn--edit" title="Edit" onClick={() => navigate('/admin/promotions/details', { state: { promotion: promo } })}>✎</button>
                                        <button className="promo-action-btn promo-action-btn--delete" title="Delete" onClick={() => setDeleteTarget(promo)}>🗑</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="promo-pagination">
                <span className="promo-result-count">{filtered.length === 0 ? 'No results' : `Showing ${Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}</span>
                <button className="promo-page-btn" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>← Previous</button>
                <span className="promo-page-divider">|</span>
                <button className="promo-page-btn" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>Next →</button>
            </div>
            {deleteTarget ? (
                <div className="promo-modal-overlay" onClick={() => setDeleteTarget(null)}>
                    <div className="promo-modal" onClick={(event) => event.stopPropagation()}>
                        <h3>Delete Promotion</h3>
                        <p>Are you sure you want to delete <strong>{deleteTarget.promoCode || deleteTarget.promotionName}</strong>? This action cannot be undone.</p>
                        <div className="promo-modal-actions">
                            <button className="promo-modal-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
                            <button className="promo-modal-delete" onClick={handleDeleteExecute}>Delete</button>
                        </div>
                    </div>
                </div>
            ) : null}
            {emailModal ? <SendEmailModal promo={emailModal.promo} defaultType={emailModal.defaultType} onClose={() => setEmailModal(null)} /> : null}
            {showAnnouncementHistory ? (
                <CampaignHistoryModal
                    emailType="announcement"
                    title="Announcement Email History"
                    subtitle="View previously sent and scheduled announcement campaigns."
                    onClose={() => setShowAnnouncementHistory(false)}
                />
            ) : null}
            {promoHistoryTarget ? (
                <CampaignHistoryModal
                    emailType="promoCode"
                    promoId={promoHistoryTarget.id}
                    title={`Promo Email History${promoHistoryTarget.promoCode ? `: ${promoHistoryTarget.promoCode}` : ''}`}
                    subtitle="View previously sent and scheduled promo email campaigns for this code."
                    onClose={() => setPromoHistoryTarget(null)}
                />
            ) : null}
        </div>
    );
}